'use client';
import { FC, useMemo, useState } from 'react';
import {
    documentsNeeded,
    LenderWithInterestRates,
} from '@/types/car-details/lender.types';
import classes from './style.module.scss';
import {
    AdminFeesIcon,
    ApprovalDuration,
    ArrowHead,
    DocumentsNeeded,
    FromDownPayment,
    UpToYears,
} from '../../../../public/Images';
import AvailableChip from './AvailableChip';
import FieldValue from './FieldValue';
import { useTranslation, useWindowDimensions } from '@/hook';
import Image from 'next/image';
import { InterestRate } from '@/types';

interface IFinancierButton {
    lender: LenderWithInterestRates;
    carSellBan: boolean;
    downpayment: number;
    fixed?: boolean;
    months: number;
}

const FinancierButton: FC<IFinancierButton> = ({
    lender,
    carSellBan,
    downpayment,
    fixed,
    months,
}) => {
    const { t } = useTranslation();
    const isAvailable = useMemo(() => {
        const interestRates = carSellBan
            ? lender.interestRateCarSellBan
            : lender.interestRate;
        return interestRates.some(
            rate =>
                downpayment === rate.downpayment &&
                rate.active &&
                lender.maxTenor >= months
        );
    }, [carSellBan, downpayment, months]);

    const hasSellBan = useMemo<boolean>(() => {
        return lender?.interestRate?.some(rate => rate.active) || false;
    }, [lender]);

    const [isOpen, setOpen] = useState<boolean>(false);
    const { width, IS_DESKTOP } = useWindowDimensions();

    const md = width >= 850 && width < 1200;
    const handleTriggerOpen = () => {
        setOpen(prev => !prev);
    };

    const formatDocumentNeeded = () => {
        if (lender?.documentsNeeded?.length === 0) {
            return t('noNeed');
        }
        return lender.documentsNeeded.reduce(
            (prev, curr) => prev + t(curr as documentsNeeded) + '\n',
            ''
        );
    };
    const minDownPayment = (interestRate: InterestRate[]): number => {
        return interestRate
            .filter(rate => rate.active)
            .sort((a, b) => a.downpayment - b.downpayment)?.[0]?.downpayment;
    };

    return (
        <div style={{ marginBottom: '16px' }}>
            <div
                className={
                    fixed || isOpen
                        ? classes.accordion_active
                        : classes.accordion
                }
                onClick={handleTriggerOpen}
            >
                <img
                    src={lender.logo}
                    alt={lender.nameEn}
                    style={{
                        maxHeight: '100%',
                        maxWidth: IS_DESKTOP ? (md ? '20%' : '15%') : '25%',
                    }}
                />
                <div className={classes.arrowContainer}>
                    {!fixed && <AvailableChip available={isAvailable} />}
                    {!fixed && (
                        <div className={classes.accordion__arrow}>
                            <Image
                                src={ArrowHead}
                                alt="ArrowHead"
                                style={{
                                    transform: isOpen
                                        ? 'scale(-1)'
                                        : 'scale(1)',
                                }}
                            ></Image>
                        </div>
                    )}
                </div>
            </div>
            <div
                className={
                    fixed || isOpen
                        ? classes.accordion_active__text
                        : classes.accordion__text
                }
            >
                <div className={classes.fieldsContainer}>
                    <FieldValue
                        label={t('adminFees')}
                        value={lender.adminFees + '%'}
                        logo={AdminFeesIcon}
                    />
                    {fixed && (
                        <FieldValue
                            label={t('fromDownpayment')}
                            value={`${minDownPayment([
                                ...lender.interestRate,
                                ...lender.interestRateCarSellBan,
                            ])}%`}
                            logo={FromDownPayment}
                        />
                    )}
                    <FieldValue
                        label={t('approvalDuration')}
                        value={t('approvalDurationValue', {
                            value: lender.approvalDuration,
                        })}
                        logo={ApprovalDuration}
                    />
                    <FieldValue
                        label={t('documentsNeeded')}
                        value={formatDocumentNeeded()}
                        logo={DocumentsNeeded}
                    />
                    <FieldValue
                        label={t('upToYears')}
                        value={`${Math.ceil(lender.maxTenor / 12)} ${t(
                            'years'
                        )}`}
                        logo={UpToYears}
                    />
                    {fixed && (
                        <FieldValue
                            label={t('noCarSellBan')}
                            value={hasSellBan ? t('Yes') : t('No')}
                            logo={UpToYears}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default FinancierButton;
